import type { PriceWithBlockMappings } from '@epilot/journey-logic-commons'
import { extractGetAgConfig } from '@epilot/pricing'
import type { BillingPeriod, PriceGetAg } from '@epilot/pricing-client'
import type { TFunction } from 'i18next'
import { useMemo } from 'react'

import { BaseTable } from './BaseTable'
import { usePriceGetAgDetailsContext } from './PriceGetAgDetailsContext'
import { getConsumptionBasedAmounts } from './utils'

export const MarkupCostsTable = () => {
  const { item, t, billingPeriod } = usePriceGetAgDetailsContext()

  const {
    basePrice,
    workPriceHT,
    workPriceNT,
    hasHTandNT,
    workPrice,
    containsAdditionalMarkups
  } = useMemo(() => {
    const basePrice = extractGetAgConfig(item, { type: 'base_price' })

    const workPriceHT = extractGetAgConfig(item, {
      type: 'work_price',
      tariffType: 'HT'
    })

    const workPriceNT = extractGetAgConfig(item, {
      type: 'work_price',
      tariffType: 'NT'
    })

    const hasHTandNT = Boolean(workPriceHT && workPriceNT)
    const workPrice = workPriceHT || workPriceNT

    const containsAdditionalMarkups =
      workPriceHT?.additional_markups_enabled ||
      workPriceNT?.additional_markups_enabled

    return {
      basePrice,
      workPriceHT,
      workPriceNT,
      hasHTandNT,
      workPrice,
      containsAdditionalMarkups
    }
  }, [item])

  return (
    <BaseTable
      rows={[
        // Show base price row
        ...getBaseOrWorkPriceMarkup({
          price: basePrice,
          t,
          item,
          billingPeriod
        }),
        // If HT and NT are present, show both HT and NT rows
        ...(hasHTandNT
          ? [
              ...getBaseOrWorkPriceMarkup({
                price: workPriceHT,
                suffixLabel: 'HT',
                t,
                item,
                billingPeriod
              }),
              ...(workPriceHT?.additional_markups_enabled &&
              workPriceHT?.additional_markups
                ? getAdditionalMarkups({
                    price: workPriceHT,
                    suffixLabel: 'HT',
                    t,
                    item,
                    billingPeriod
                  })
                : []),
              ...getBaseOrWorkPriceMarkup({
                price: workPriceNT,
                suffixLabel: 'NT',
                t,
                item,
                billingPeriod
              }),
              ...(workPriceNT?.additional_markups_enabled &&
              workPriceNT?.additional_markups
                ? getAdditionalMarkups({
                    price: workPriceNT,
                    suffixLabel: 'NT',
                    t,
                    item,
                    billingPeriod
                  })
                : [])
            ]
          : [
              // Show single work price row (HT or NT, whichever exists)
              ...getBaseOrWorkPriceMarkup({
                price: workPrice,
                t,
                item,
                billingPeriod
              }),
              ...(workPrice?.additional_markups_enabled &&
              workPrice?.additional_markups
                ? getAdditionalMarkups({
                    price: workPrice,
                    t,
                    item,
                    billingPeriod
                  })
                : [])
            ])
      ]}
      title={t(
        `getag_details_table.groups.sales_and_procurement_costs.title${containsAdditionalMarkups ? '_with_energy' : ''}`,
        'Vetriebs- und Beschaffungskosten'
      )}
    />
  )
}

const getBaseOrWorkPriceMarkup = ({
  price,
  suffixLabel,
  t,
  item,
  billingPeriod
}: {
  price: PriceGetAg | undefined
  suffixLabel?: string
  t: TFunction
  item: PriceWithBlockMappings
  billingPeriod: BillingPeriod
}) => {
  return [
    {
      label:
        price?.type === 'work_price'
          ? t(
              'getag_details_table.groups.sales_and_procurement_costs.fees.markup_work_price',
              'Vertriebsanteil Arbeitspreis'
            ) + (suffixLabel ? ` (${suffixLabel})` : '')
          : t(
              'getag_details_table.groups.sales_and_procurement_costs.fees.markup_base_price',
              'Vertriebsanteil Grundpreis'
            ),
      isEmpty: !price?.markup_amount,
      ...(price?.type === 'work_price'
        ? {
            unitAmountDecimal: price?.markup_amount_gross_decimal,
            ...getConsumptionBasedAmounts(
              price?.markup_amount_gross_decimal,
              price?.tariff_type === 'HT'
                ? item.getag_price?.inputs.consumptionHT
                : item.getag_price?.inputs.consumptionNT,
              billingPeriod
            )
          }
        : {
            amountDecimal: price?.markup_amount_gross_decimal
          })
    }
  ]
}

const getAdditionalMarkups = ({
  price,
  suffixLabel,
  t,
  item,
  billingPeriod
}: {
  price: PriceGetAg
  suffixLabel?: string
  t: TFunction
  item: PriceWithBlockMappings
  billingPeriod: BillingPeriod
}) => {
  /*
    Currently, only procurement markup is relevant.
    If we need to add more markups, we can add them here.
  */
  const procurementMarkup = price?.additional_markups?.procurement

  if (!procurementMarkup) {
    return []
  }

  const { yearlyAmountDecimal, amountDecimal } = getConsumptionBasedAmounts(
    procurementMarkup?.amount_gross_decimal,
    price?.tariff_type === 'HT'
      ? item.getag_price?.inputs.consumptionHT
      : item.getag_price?.inputs.consumptionNT,
    billingPeriod
  )

  return [
    {
      label:
        t(
          `getag_details_table.groups.sales_and_procurement_costs.fees.markup_procurement`,
          'Energiebeschaffungskosten'
        ) + (suffixLabel ? ` (${suffixLabel})` : ''),
      amountDecimal,
      yearlyAmountDecimal,
      isEmpty: !procurementMarkup?.amount,
      unitAmountDecimal: procurementMarkup?.amount_gross_decimal
    }
  ]
}
